import React from "react";
import ReactDOM from "react-dom";
import BlocksSelector from "./BlocksSelector";
import { useDispatch } from "react-redux";
import { setBlockRange } from "../../store/state/BlockSuggestion";
import { X } from "lucide-react";
import { ReactComponent as SelectBlock } from "../../icons/Select_block.svg";

export const TimeDivider = [
  {
    name: "Off-Peak",
    range: "12:00 AM – 6:00 AM",
    startBlock: 1,
    endBlock: 24,
  },
  { name: "Daytime", range: "6:00 AM – 6:00 PM", startBlock: 25, endBlock: 72 },
  {
    name: "Evening + Late Night",
    range: "6:00 PM – 12:00 AM",
    startBlock: 73,
    endBlock: 96,
  },
];

export const BlockSuggestions = [
  {
    label: "Morning Peak",
    range: "6:00 AM – 10:00 AM",
    startBlock: 25,
    endBlock: 40,
  },
  {
    label: "Midday Dip",
    range: "12:00 PM – 2:00 PM",
    startBlock: 49,
    endBlock: 56,
  },
  {
    label: "Evening Peak",
    range: "6:00 PM – 10:00 PM",
    startBlock: 73,
    endBlock: 88,
  },
  {
    label: "Off-Peak Night",
    range: "2:00 AM – 6:00 AM",
    startBlock: 9,
    endBlock: 24,
  },
];

type BlockRangeSelectorProps = {
  blocks: number[];
  placeholder: string;
  selectedOptions: number[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<number[]>>;
  blockMinutes?: number;
};

const buildTimeDivider = (blockMinutes: number) => {
  const blocksPerHour = 60 / blockMinutes;
  return [
    {
      name: "Off-Peak",
      range: "12:00 AM – 6:00 AM",
      startBlock: 1,
      endBlock: 6 * blocksPerHour,
    },
    {
      name: "Daytime",
      range: "6:00 AM – 6:00 PM",
      startBlock: 6 * blocksPerHour + 1,
      endBlock: 18 * blocksPerHour,
    },
    {
      name: "Evening + Late Night",
      range: "6:00 PM – 12:00 AM",
      startBlock: 18 * blocksPerHour + 1,
      endBlock: 24 * blocksPerHour,
    },
  ];
};

const buildBlockSuggestions = (blockMinutes: number) => {
  const bph = 60 / blockMinutes;
  return [
    {
      label: "Morning Peak",
      range: "6:00 AM – 10:00 AM",
      startBlock: 6 * bph + 1,
      endBlock: 10 * bph,
    },
    {
      label: "Midday Dip",
      range: "12:00 PM – 2:00 PM",
      startBlock: 12 * bph + 1,
      endBlock: 14 * bph,
    },
    {
      label: "Evening Peak",
      range: "6:00 PM – 10:00 PM",
      startBlock: 18 * bph + 1,
      endBlock: 22 * bph,
    },
    {
      label: "Off-Peak Night",
      range: "2:00 AM – 6:00 AM",
      startBlock: 2 * bph + 1,
      endBlock: 6 * bph,
    },
  ];
};

const BlockRangeSelector: React.FC<BlockRangeSelectorProps> = ({
  blocks,
  placeholder,
  selectedOptions,
  setSelectedOptions,
  blockMinutes = 15,
}) => {
  const dynamicSuggestions = React.useMemo(
    () => buildBlockSuggestions(blockMinutes),
    [blockMinutes],
  );
  const [mounted, setMounted] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [finalRange, setFinalRange] = React.useState<Record<string, unknown>>(
    {},
  );

  const openPicker = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setMounted(true);
    requestAnimationFrame(() => setIsOpen(true));
  };

  const closePicker = () => {
    setIsOpen(false);
    closeTimeoutRef.current = setTimeout(() => setMounted(false), 250);
  };

  // CRITICAL FIX: Sync finalRange when selectedOptions changes externally
  React.useEffect(() => {
    if (selectedOptions.length === 0) {
      setFinalRange({});
    } else {
      // When blocks are removed externally, recalculate finalRange
      const newFinalRange: Record<string, number[]> = {};

      // Group consecutive blocks into ranges
      if (selectedOptions.length > 0) {
        const sortedBlocks = [...selectedOptions].sort((a, b) => a - b);
        let rangeStart = sortedBlocks[0];
        let rangeEnd = sortedBlocks[0];
        let currentRange = [sortedBlocks[0]];

        for (let i = 1; i < sortedBlocks.length; i++) {
          if (sortedBlocks[i] === rangeEnd + 1) {
            // Consecutive block, extend the range
            rangeEnd = sortedBlocks[i];
            currentRange.push(sortedBlocks[i]);
          } else {
            // Non-consecutive, save current range and start new one
            const rangeKey = `${rangeStart}-${rangeEnd}`;
            newFinalRange[rangeKey] = currentRange;

            rangeStart = sortedBlocks[i];
            rangeEnd = sortedBlocks[i];
            currentRange = [sortedBlocks[i]];
          }
        }

        // Save the last range
        const rangeKey = `${rangeStart}-${rangeEnd}`;
        newFinalRange[rangeKey] = currentRange;
      }

      setFinalRange(newFinalRange);
    }
  }, [selectedOptions]);

  const removeTag = (blockToRemove: number, indexToRemove: number) => {
    setSelectedOptions((prev) => {
      const newArray = prev.filter((_, index) => index !== indexToRemove);
      return newArray;
    });
  };

  const clearAllBlocks = () => {
    setSelectedOptions([]);
  };

  const hasSelection = selectedOptions.length > 0;

  return (
    <div className="relative">
      <div
        className={`flex items-center gap-1.5 px-2.5 border transition-all duration-150 cursor-pointer select-none ${
          hasSelection
            ? "border-[#D1D5DB] bg-white shadow-sm hover:border-[#9CA3AF]"
            : "border-[#D1D5DB] bg-white hover:border-[#9CA3AF] hover:bg-gray-50"
        }`}
        style={{ height: 37, minWidth: 140, maxWidth: 200, boxSizing: "border-box", borderRadius: 10 }}
        onClick={() => (mounted ? closePicker() : openPicker())}
      >
        <SelectBlock
          className="w-[16px] h-[16px] flex-shrink-0 text-[#A8A29F]"
          onClick={(e: React.MouseEvent<SVGSVGElement>) => {
            e.preventDefault();
            e.stopPropagation();
            mounted ? closePicker() : openPicker();
          }}
        />

        <div
          className="scroll-thin flex-1 min-w-0"
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            overflowX: "hidden",
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
        >
          {hasSelection ? (
            <div className="flex flex-wrap gap-1 items-center w-full">
              {selectedOptions.map((block, index) => (
                <div
                  key={`${block}-${index}`}
                  className="flex items-center gap-0.5 bg-[#FDE7D9] text-[#A44E1E] border border-[#f5c4a5] px-1 py-0.5 rounded-md whitespace-nowrap flex-shrink-0"
                >
                  <span className="text-[10px] font-semibold">{block}</span>
                  <button
                    type="button"
                    className="cursor-pointer hover:bg-red-400 hover:text-white rounded text-[#A44E1E]/60 text-xs leading-none px-0.5 transition-colors flex-shrink-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeTag(block, index);
                    }}
                    style={{ minWidth: "12px", minHeight: "12px" }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-[12px] text-[#9ca3af] font-medium">{placeholder}</span>
          )}
        </div>

        {hasSelection && (
          <X
            size={14}
            className="text-[#A8A29F] flex-shrink-0 hover:text-red-500 transition-colors"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clearAllBlocks();
            }}
          />
        )}
      </div>

      {mounted &&
        ReactDOM.createPortal(
          <>
            <style>{`
              .brs-backdrop {
                transition: opacity 250ms ease;
              }
              .brs-backdrop-enter { opacity: 0; }
              .brs-backdrop-open  { opacity: 1; }

              .brs-modal {
                transition: opacity 250ms ease, transform 250ms ease;
              }
              .brs-modal-enter { opacity: 0; transform: scale(0.92) translateY(12px); }
              .brs-modal-open  { opacity: 1; transform: scale(1)    translateY(0);    }
            `}</style>

            {/* Backdrop */}
            <div
              className={`fixed inset-0 z-[9998] brs-backdrop ${isOpen ? "brs-backdrop-open" : "brs-backdrop-enter"}`}
              style={{
                background: "rgba(55,57,60,0.6)",
                backdropFilter: "blur(2px)",
              }}
              onClick={closePicker}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
              <div
                className={`pointer-events-auto brs-modal ${isOpen ? "brs-modal-open" : "brs-modal-enter"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <BlockSelectionModel
                  setSelectedOptions={setSelectedOptions}
                  selectedOptions={selectedOptions}
                  blocks={blocks}
                  setOpen={closePicker}
                  setFinalRange={setFinalRange}
                  finalRange={finalRange}
                  blockMinutes={blockMinutes}
                  suggestions={dynamicSuggestions}
                />
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

type BlockSelectionModelProps = {
  blocks: number[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<number[]>>;
  setOpen: () => void;
  selectedOptions: number[];
  finalRange: Record<string, unknown>;
  setFinalRange: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  blockMinutes: number;
  suggestions: {
    label: string;
    range: string;
    startBlock: number;
    endBlock: number;
  }[];
};

function BlockSelectionModel({
  blocks,
  setSelectedOptions,
  setOpen,
  selectedOptions,
  finalRange,
  setFinalRange,
  blockMinutes,
  suggestions,
}: BlockSelectionModelProps) {
  const dispatch = useDispatch();

  return (
    <div
      className="transition-all border ease-in-out duration-200 w-[800px] text-sm text-[#A8A29F] bg-[#FAFAFA] rounded-xl shadow-lg relative overflow-hidden"
      style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
    >
      <div className="flex w-full" style={{ minHeight: 0, flex: "1 1 auto" }}>
        <div
          className="p-5 rounded-lg w-[35%] bg-gray-50 overflow-y-auto"
          style={{ maxHeight: "90vh" }}
        >
          <ul className="mt-2 flex flex-col gap-4">
            <h5 className="text-gray-800 text-xs font-medium">
              Suggested Blocks
            </h5>
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => {
                  const range: number[] = [];
                  for (
                    let i = suggestion.startBlock;
                    i <= suggestion.endBlock;
                    i++
                  ) {
                    range.push(i);
                  }
                  // setSelectedOptions((prev) => [...prev, ...range]);

                  dispatch(
                    setBlockRange({
                      startBlock: suggestion.startBlock,
                      endBlock: suggestion.endBlock,
                      isClicked: true,
                    }),
                  );
                }}
                onMouseEnter={() => {
                  dispatch(
                    setBlockRange({
                      startBlock: suggestion.startBlock,
                      endBlock: suggestion.endBlock,
                    }),
                  );
                }}
                onMouseLeave={() => {
                  dispatch(setBlockRange({ startBlock: null, endBlock: null }));
                }}
                className="text-xs cursor-pointer"
              >
                <button
                  type="button"
                  className="text-start p-2 w-full hover:border-primary border border-transparent rounded-lg hover:text-primary hover:bg-primary/5 transition-colors ease-in-out duration-200"
                >
                  {suggestion.label}
                  <br />
                  <span>{suggestion.range}</span>
                </button>
              </li>
            ))}
          </ul>
          {/* NEW: Instructions banner */}
          {/* <div className="col-span-3 mb-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Instructions:</strong>
              <br />
              <span className="font-medium">Range Selection:</span>
              <br /> Single-click start block, hover to preview, single-click
              end block
              <br />
              <br />
              <span className="font-medium">Individual Selection:</span>
              <br />
              Double-click any block to select/deselect it individually
            </p>
          </div> */}
        </div>

        <div
          className="w-[65%] bg-white rounded-xl shadow-lg flex flex-col"
          style={{ minHeight: 0 }}
        >
          <BlocksSelector
            key={JSON.stringify(finalRange)}
            setOpen={setOpen}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            blocks={blocks}
            finalRange={finalRange}
            setFinalRange={setFinalRange}
            blockMinutes={blockMinutes}
          />
        </div>
      </div>
    </div>
  );
}

export default BlockRangeSelector;
